import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase';
import {
  collection, addDoc, doc, setDoc, getDoc, serverTimestamp, increment
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { DIFFICULTY_LEVELS, PREFECTURES, VALIDATION_RULES } from '../utils/constants';
import { validateMaxLength, validateParticipants } from '../utils/helpers';
import { compressImageFile } from '../utils/imageHelpers';
import { guardContentSubmission, showModerationWarning } from '../utils/moderation';
import { showToast } from '../utils/toast';
import './CreateRecruitment.css';

const TRIP_TYPE_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'day_hike', label: '日帰り' },
  { value: 'hut_stay', label: '小屋泊' },
  { value: 'tent_stay', label: 'テント泊' },
  { value: 'multi_day', label: '縦走・複数日' }
];

const TRANSPORT_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'public_transport', label: '公共交通' },
  { value: 'car', label: '車（レンタカー）' },
  { value: 'carpool', label: '車（相乗り）' },
  { value: 'mixed', label: '公共交通・車どちらも可' }
];

const DANGER_OPTIONS = [
  { value: 'chain', label: '鎖場' },
  { value: 'rock', label: '岩場' },
  { value: 'snow', label: '雪渓・残雪' },
  { value: 'river_crossing', label: '渡渉' },
  { value: 'steep_descent', label: '急登・急下降' }
];

const EQUIPMENT_REQUIREMENT_OPTIONS = [
  { value: 'helmet', label: 'ヘルメット' },
  { value: 'chain_spike', label: 'チェーンスパイク' },
  { value: 'trekking_pole', label: 'トレッキングポール' },
  { value: 'crampon', label: 'アイゼン' },
  { value: 'first_aid', label: 'ファーストエイドキット' },
  { value: 'hardshell', label: 'ハードシェル' },
  { value: 'avalanche_gear', label: '雪崩装備' }
];

const MEETING_AREA_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'station', label: '駅集合' },
  { value: 'trailhead', label: '登山口集合' },
  { value: 'local', label: '現地集合' },
  { value: 'other', label: 'その他' }
];

const EXPERIENCE_LEVEL_TARGET_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'beginner_welcome', label: '初心者歓迎' },
  { value: 'beginner', label: '初級者向け' },
  { value: 'intermediate', label: '中級者向け' },
  { value: 'advanced', label: '上級者向け' }
];

const CAR_SHARE_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'available', label: '相乗り可' },
  { value: 'not_available', label: '相乗り不可' },
  { value: 'consult', label: '相談可' }
];

const WEATHER_THRESHOLD_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'light_rain_ok', label: '小雨決行' },
  { value: 'rain_cancel', label: '雨天中止' },
  { value: 'storm_cancel', label: '荒天中止' },
  { value: 'decide_previous_day', label: '前日判断' }
];

const ESTIMATED_BUDGET_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'under_3000', label: '3,000円未満' },
  { value: '3000_5000', label: '3,000〜5,000円' },
  { value: '5000_10000', label: '5,000〜10,000円' },
  { value: '10000_20000', label: '10,000〜20,000円' },
  { value: 'over_20000', label: '20,000円以上' }
];

const GENDER_RATIO_OPTIONS = [
  '未設定', '男性多め', '女性多め', '半々くらい', '男性のみ', '女性のみ', 'その他'
];

const isBeginnerDangerous = (difficulty, dangerFlags = []) => {
  return difficulty === 'advanced' || (Array.isArray(dangerFlags) && dangerFlags.length > 0);
};

const CHECKLIST_URL_PREFIX = 'https://yamano-media.com/blog/mountain-checker-app/';

const EMPTY_FORM = {
  recruitmentType: '',
  mountainName: '',
  route: '',
  departureDate: '',
  maxParticipants: 1,
  difficulty: 'beginner',
  description: '',
  prefecture: '',
  meetingPoint: '',
  equipment: '',
  requirements: '',
  eventName: '',
  checklistUrl: '',
  tripType: '',
  transportMode: '',
  walkingHours: '',
  elevationGain: '',
  dangerFlags: [],
  equipmentRequirements: [],
  meetingAreaType: '',
  experienceLevelTarget: '',
  carShareAvailable: '',
  weatherThreshold: '',
  estimatedBudgetBand: '',
  safetyAcknowledged: false,
  minParticipants: 3,
  genderRatioSingle: '',
  participantCondition: 'anyone',
  groupName: '',
  activityFrequency: '',
  activityFrequencyOther: '',
  currentMembers: 1,
  genderRatio: '',
  ageGroup: '',
  activityStyle: '',
  homepage: ''
};

function CreateRecruitment() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [recruitmentImageFile, setRecruitmentImageFile] = useState(null);
  const [recruitmentImagePreview, setRecruitmentImagePreview] = useState('');
  const imageInputRef = useRef(null);
  const fieldRefMap = useRef({});
  const [formData, setFormData] = useState(EMPTY_FORM);

  const setFieldError = (field, msg) => {
    setFieldErrors({ [field]: msg });
    setTimeout(() => {
      fieldRefMap.current[field]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const fieldRef = (name) => (el) => { if (el) fieldRefMap.current[name] = el; };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? (value === '' ? '' : Number(value)) : value
    }));
  };

  const handleMultiSelectChange = (name, value) => {
    setFormData(prev => {
      const current = Array.isArray(prev[name]) ? prev[name] : [];
      const next = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      return { ...prev, [name]: next };
    });
  };

  const handleActivityFrequencyChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({
      ...prev,
      activityFrequency: value,
      activityFrequencyOther: value === 'other' ? prev.activityFrequencyOther : ''
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setFieldError('image', '画像は10MB以下にしてください。');
      return;
    }
    try {
      setFieldErrors({});
      const compressedFile = await compressImageFile(file, {
        maxWidth: 1280, maxHeight: 1280, quality: 0.85, outputType: 'image/jpeg'
      });
      setRecruitmentImageFile(compressedFile);
      setRecruitmentImagePreview(URL.createObjectURL(compressedFile));
    } catch (err) {
      console.error('画像圧縮エラー:', err);
      setFieldError('image', '画像の圧縮に失敗しました。別の画像で再試行してください。');
    }
  };

  const err = (field, message) => ({ field, message });

  const validateSingle = () => {
    if (!formData.mountainName.trim()) return err('mountainName', '山名を入力してください。');
    if (!formData.departureDate) return err('departureDate', '出発日を選択してください。');
    if (!formData.prefecture) return err('prefecture', '都道府県を選択してください。');
    if (formData.walkingHours === '' || formData.walkingHours === null || isNaN(Number(formData.walkingHours)) || Number(formData.walkingHours) <= 0) {
      return err('walkingHours', '標準CT（コースタイム）を入力してください。');
    }
    if (!formData.safetyAcknowledged) {
      return err('safetyAcknowledged', '安全確認のチェックボックスにチェックを入れてください。');
    }
    const dateObj = new Date(formData.departureDate);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (dateObj < today) return err('departureDate', '出発日は今日以降の日付を選択してください。');

    const mountainNameV = validateMaxLength(formData.mountainName, VALIDATION_RULES.MOUNTAIN_NAME_MAX_LENGTH, '山名');
    if (!mountainNameV.isValid) return err('mountainName', mountainNameV.message);

    const routeV = validateMaxLength(formData.route, VALIDATION_RULES.ROUTE_MAX_LENGTH, 'ルート');
    if (!routeV.isValid) return err('route', routeV.message);

    const meetingV = validateMaxLength(formData.meetingPoint, VALIDATION_RULES.MEETING_POINT_MAX_LENGTH, '集合場所');
    if (!meetingV.isValid) return err('meetingPoint', meetingV.message);

    const equipV = validateMaxLength(formData.equipment, VALIDATION_RULES.EQUIPMENT_MAX_LENGTH, '持参装備');
    if (!equipV.isValid) return err('equipment', equipV.message);

    const participantsV = validateParticipants(formData.maxParticipants);
    if (!participantsV.isValid) return err('maxParticipants', participantsV.message);

    if (formData.checklistUrl && !formData.checklistUrl.startsWith(CHECKLIST_URL_PREFIX)) {
      return err('checklistUrl', `装備チェックリストURLは ${CHECKLIST_URL_PREFIX} から始まるURLのみ入力できます。`);
    }
    return null;
  };

  const validateGroup = () => {
    if (!formData.groupName.trim()) return err('groupName', '団体名を入力してください。');
    if (!formData.prefecture) return err('prefecture', '都道府県を選択してください。');
    if (!formData.activityFrequency) return err('activityFrequency', '活動頻度を選択してください。');
    if (formData.activityFrequency === 'other' && !formData.activityFrequencyOther.trim()) {
      return err('activityFrequencyOther', '活動頻度（その他）を入力してください。');
    }
    if (!formData.activityStyle) return err('activityStyle', '活動スタイルを選択してください。');
    if (!formData.description.trim()) return err('description', '詳細説明を入力してください。');
    if (!formData.currentMembers || formData.currentMembers < 1) {
      return err('currentMembers', '現在のメンバー数は1以上で入力してください。');
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      setFieldError('_form', 'ログインが必要です。');
      return;
    }

    setFieldErrors({});
    const validationError = formData.recruitmentType === 'group' ? validateGroup() : validateSingle();
    if (validationError) {
      setFieldError(validationError.field, validationError.message);
      return;
    }

    const descV = validateMaxLength(formData.description, VALIDATION_RULES.DESCRIPTION_MAX_LENGTH, '詳細');
    if (!descV.isValid) { setFieldError('description', descV.message); return; }

    setLoading(true);
    try {
      const moderation = await guardContentSubmission({
        action: 'recruitment_create',
        contentType: formData.recruitmentType === 'group' ? 'group_recruitment' : 'single_recruitment',
        texts: [
          formData.mountainName, formData.route, formData.description,
          formData.meetingPoint, formData.equipment, formData.eventName,
          formData.groupName, formData.activityFrequencyOther
        ],
        metadata: { recruitmentType: formData.recruitmentType }
      });
      showModerationWarning(moderation);

      const userProfileDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userProfile = userProfileDoc.exists() ? userProfileDoc.data() : {};
      const organizerInfo = {
        nickname: userProfile.nickname || currentUser.displayName || '名無しの登山者',
        photoURL: userProfile.photoURL || currentUser.photoURL || '',
        email: currentUser.email || '',
        uid: currentUser.uid
      };

      let imageUrl = '';
      if (recruitmentImageFile) {
        const storageRef = ref(storage, `recruitment_images/${currentUser.uid}_${Date.now()}`);
        await uploadBytes(storageRef, recruitmentImageFile, {
          contentType: recruitmentImageFile.type || 'image/jpeg'
        });
        imageUrl = await getDownloadURL(storageRef);
      }

      const recruitmentData = {
        recruitmentType: formData.recruitmentType,
        userId: currentUser.uid,
        organizer: organizerInfo,
        status: 'active',
        applicationCount: 0,
        imageUrl,
        description: formData.description,
        prefecture: formData.prefecture,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      if (formData.recruitmentType === 'group') {
        Object.assign(recruitmentData, {
          groupName: formData.groupName,
          maxParticipants: 0,
          activityFrequency: formData.activityFrequency,
          activityFrequencyOther: formData.activityFrequencyOther,
          currentMembers: formData.currentMembers,
          genderRatio: formData.genderRatio,
          ageGroup: formData.ageGroup,
          activityStyle: formData.activityStyle,
          homepage: formData.homepage
        });
      } else {
        Object.assign(recruitmentData, {
          mountainName: formData.mountainName,
          route: formData.route,
          departureDate: new Date(formData.departureDate),
          maxParticipants: formData.maxParticipants,
          difficulty: formData.difficulty,
          meetingPoint: formData.meetingPoint,
          equipment: formData.equipment,
          requirements: formData.requirements,
          eventName: formData.eventName,
          checklistUrl: formData.checklistUrl,
          tripType: formData.tripType,
          transportMode: formData.transportMode,
          walkingHours: formData.walkingHours,
          elevationGain: formData.elevationGain,
          dangerFlags: formData.dangerFlags,
          equipmentRequirements: formData.equipmentRequirements,
          meetingAreaType: formData.meetingAreaType,
          experienceLevelTarget: formData.experienceLevelTarget,
          carShareAvailable: formData.carShareAvailable,
          weatherThreshold: formData.weatherThreshold,
          estimatedBudgetBand: formData.estimatedBudgetBand,
          minParticipants: formData.minParticipants,
          genderRatioSingle: formData.genderRatioSingle,
          participantCondition: formData.participantCondition
        });
      }

      const docRef = await addDoc(collection(db, 'recruitments'), recruitmentData);

      await setDoc(
        doc(db, 'userStats', currentUser.uid),
        { recruitedCount: increment(1) },
        { merge: true }
      );

      showToast({ message: '募集を作成しました！', type: 'success' });
      navigate(`/recruitments/${docRef.id}`);
    } catch (err) {
      console.error('募集の作成に失敗しました:', err);
      setFieldError('_form', '募集の作成に失敗しました。もう一度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('作成を中止しますか？')) navigate('/recruitments');
  };

  return (
    <div className="create-recruitment-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
          <p>登録中...</p>
        </div>
      )}
      <div className="card">
        <h1 className="page-title">募集を作成</h1>

        {fieldErrors._form && (
          <div className="alert alert-danger">{fieldErrors._form}</div>
        )}

        <form onSubmit={handleSubmit} className="recruitment-form" noValidate>
          <div className="form-section">
            <h2>募集タイプ</h2>
            <p className="form-guide">まず募集タイプを選択してください</p>
            <div className="recruitment-type-selector">
              <button
                type="button"
                id="type-single-btn"
                className={`type-btn ${formData.recruitmentType === 'single' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, recruitmentType: 'single' }))}
              >
                <span>🏔️</span>
                <span className="btn-text">単発募集</span>
              </button>
              <button
                type="button"
                id="type-group-btn"
                className={`type-btn ${formData.recruitmentType === 'group' ? 'active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, recruitmentType: 'group' }))}
              >
                <span>👥</span>
                <span className="btn-text">所属メンバー募集</span>
              </button>
            </div>
          </div>

          {formData.recruitmentType === 'group' && (
            <>
              <div className="form-section">
                <h2>所属メンバー募集情報</h2>

                <div className="form-group" ref={fieldRef('groupName')}>
                  <label className="form-label">団体名 *</label>
                  <input type="text" name="groupName" className="form-control"
                    value={formData.groupName} onChange={handleChange}
                    required placeholder="例: 東京山岳会" />
                  {fieldErrors.groupName && <p className="field-error">{fieldErrors.groupName}</p>}
                </div>

                <div className="form-row">
                  <div className="form-group" ref={fieldRef('prefecture')}>
                    <label className="form-label">都道府県 *</label>
                    <select name="prefecture" className="form-control"
                      value={formData.prefecture} onChange={handleChange} required>
                      <option value="">選択してください</option>
                      {PREFECTURES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    {fieldErrors.prefecture && <p className="field-error">{fieldErrors.prefecture}</p>}
                  </div>

                  <div className="form-group" ref={fieldRef('activityFrequency')}>
                    <label className="form-label">活動頻度 *</label>
                    <select name="activityFrequency" className="form-control"
                      value={formData.activityFrequency} onChange={handleActivityFrequencyChange} required>
                      <option value="">選択してください</option>
                      <option value="weekly">週1回</option>
                      <option value="biweekly">月2回</option>
                      <option value="monthly">月1回</option>
                      <option value="seasonal">季節ごと</option>
                      <option value="irregular">不定期</option>
                      <option value="other">その他</option>
                    </select>
                    {fieldErrors.activityFrequency && <p className="field-error">{fieldErrors.activityFrequency}</p>}
                  </div>
                </div>

                {formData.activityFrequency === 'other' && (
                  <div className="form-group" ref={fieldRef('activityFrequencyOther')}>
                    <label className="form-label">活動頻度（その他）</label>
                    <input type="text" name="activityFrequencyOther" className="form-control"
                      value={formData.activityFrequencyOther} onChange={handleChange}
                      placeholder="例: 年10回程度、土日のみなど" />
                    {fieldErrors.activityFrequencyOther && <p className="field-error">{fieldErrors.activityFrequencyOther}</p>}
                  </div>
                )}

                <div className="form-row">
                  <div className="form-group" ref={fieldRef('currentMembers')}>
                    <label className="form-label">現在のメンバー数 *</label>
                    <input type="number" name="currentMembers" className="form-control"
                      value={formData.currentMembers} onChange={handleChange}
                      required min="1" placeholder="例: 15" />
                    {fieldErrors.currentMembers && <p className="field-error">{fieldErrors.currentMembers}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">主な年齢層</label>
                    <select name="ageGroup" className="form-control"
                      value={formData.ageGroup} onChange={handleChange}>
                      <option value="">選択してください</option>
                      <option value="20s">20代</option>
                      <option value="30s">30代</option>
                      <option value="40s">40代</option>
                      <option value="50s">50代</option>
                      <option value="60s">60代以上</option>
                      <option value="mixed">幅広い年齢層</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">男女比</label>
                    <select name="genderRatio" className="form-control"
                      value={formData.genderRatio} onChange={handleChange}>
                      {GENDER_RATIO_OPTIONS.map(o => (
                        <option key={o} value={o === '未設定' ? '' : o}>{o}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group" ref={fieldRef('activityStyle')}>
                    <label className="form-label">活動スタイル *</label>
                    <select name="activityStyle" className="form-control"
                      value={formData.activityStyle} onChange={handleChange} required>
                      <option value="">選択してください</option>
                      <option value="beginner-friendly">初心者歓迎</option>
                      <option value="fun-oriented">楽しさ重視</option>
                      <option value="satoyama-focused">里山中心</option>
                      <option value="hyakumeizan-focused">百名山中心</option>
                      <option value="lightweight-oriented">軽量志向</option>
                      <option value="senior-friendly">シニア向け</option>
                      <option value="mixed">様々なスタイル</option>
                    </select>
                    {fieldErrors.activityStyle && <p className="field-error">{fieldErrors.activityStyle}</p>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">ホームページ</label>
                  <input type="url" name="homepage" className="form-control"
                    value={formData.homepage} onChange={handleChange}
                    placeholder="https://example.com" />
                </div>

                <div className="form-group" ref={fieldRef('description')}>
                  <label className="form-label">詳細説明 *</label>
                  <textarea name="description" className="form-control"
                    value={formData.description} onChange={handleChange}
                    required rows={6}
                    placeholder="団体の紹介、活動内容、歓迎するメンバー像などを詳しく記載してください。" />
                  {fieldErrors.description && <p className="field-error">{fieldErrors.description}</p>}
                </div>
              </div>
            </>
          )}

          {formData.recruitmentType === 'single' && (
            <>
              <div className="form-section">
                <h2>単発募集情報</h2>

                <div className="form-group">
                  <label className="form-label">イベント名（任意）</label>
                  <input type="text" name="eventName" className="form-control"
                    value={formData.eventName} onChange={handleChange}
                    placeholder="例: 富士山ご来光ツアー 2026夏" />
                  <p className="form-help">山行に名前をつけたい場合に入力してください</p>
                </div>

                <div className="form-row">
                  <div className="form-group" ref={fieldRef('mountainName')}>
                    <label className="form-label">山名 *</label>
                    <input type="text" name="mountainName" className="form-control"
                      value={formData.mountainName} onChange={handleChange}
                      required placeholder="例: 富士山" />
                    {fieldErrors.mountainName && <p className="field-error">{fieldErrors.mountainName}</p>}
                  </div>

                  <div className="form-group" ref={fieldRef('route')}>
                    <label className="form-label">登山ルート</label>
                    <input type="text" name="route" className="form-control"
                      value={formData.route} onChange={handleChange}
                      placeholder="例: 吉田ルート" />
                    {fieldErrors.route && <p className="field-error">{fieldErrors.route}</p>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group" ref={fieldRef('departureDate')}>
                    <label className="form-label">出発予定日 *</label>
                    <input type="date" name="departureDate" className="form-control"
                      value={formData.departureDate} onChange={handleChange}
                      required min={new Date().toISOString().split('T')[0]} />
                    {fieldErrors.departureDate && <p className="field-error">{fieldErrors.departureDate}</p>}
                  </div>

                  <div className="form-group" ref={fieldRef('maxParticipants')}>
                    <label className="form-label">募集人数 *</label>
                    <input type="number" name="maxParticipants" className="form-control"
                      value={formData.maxParticipants} onChange={handleChange}
                      required min="1" max="20" />
                    {fieldErrors.maxParticipants && <p className="field-error">{fieldErrors.maxParticipants}</p>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">難易度 *</label>
                    <select name="difficulty" className="form-control"
                      value={formData.difficulty} onChange={handleChange} required>
                      {Object.values(DIFFICULTY_LEVELS).map(({ id: diffId, label }) => (
                        <option key={diffId} value={diffId}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group" ref={fieldRef('prefecture')}>
                    <label className="form-label">都道府県 *</label>
                    <select name="prefecture" className="form-control"
                      value={formData.prefecture} onChange={handleChange} required>
                      <option value="">選択してください</option>
                      {PREFECTURES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    {fieldErrors.prefecture && <p className="field-error">{fieldErrors.prefecture}</p>}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">持参装備</label>
                  <textarea name="equipment" className="form-control"
                    value={formData.equipment} onChange={handleChange}
                    rows={3} placeholder="必要な装備、服装、注意事項などを記載してください。" />
                </div>

                <div className="form-group">
                  <label className="form-label">集合場所・時間</label>
                  <input type="text" name="meetingPoint" className="form-control"
                    value={formData.meetingPoint} onChange={handleChange}
                    placeholder="例: 新宿駅西口 8:00" />
                </div>

                <div className="form-group safety-notice" ref={fieldRef('safetyAcknowledged')}>
                  <strong>安全確認</strong>
                  <label className="safety-notice__checkbox-label">
                    <input type="checkbox" name="safetyAcknowledged"
                      checked={formData.safetyAcknowledged} onChange={handleChange} />
                    募集者が計画責任者であること、登山には危険があり参加判断は各自の自己責任で行うこと、登山計画書の提出を忘れずに案内することを確認しました。
                  </label>
                  {fieldErrors.safetyAcknowledged && (
                    <p className="field-error">{fieldErrors.safetyAcknowledged}</p>
                  )}
                  {isBeginnerDangerous(formData.difficulty, formData.dangerFlags) && (
                    <p className="form-help safety-notice__danger-text">
                      この山は初心者には危険です。募集文に必要経験・危険箇所・撤退判断を明記してください。
                    </p>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group" ref={fieldRef('walkingHours')}>
                    <label className="form-label">
                      標準CT *
                      <span
                        className="tooltip-icon"
                        tabIndex="0"
                        role="note"
                        data-tooltip="標準コースタイムは、休憩を除いた一般的な歩行時間の目安です。山と高原地図・YAMAP等のコースタイムを参考に、時間単位で入力してください。"
                        title="標準コースタイムの説明"
                      >
                        ?
                      </span>
                    </label>
                    <input type="number" name="walkingHours" className="form-control"
                      value={formData.walkingHours} onChange={handleChange}
                      min="0" step="0.5" placeholder="例: 5.5" required />
                    <p className="form-help">標準コースタイムを時間で入力してください。</p>
                    {fieldErrors.walkingHours && <p className="field-error">{fieldErrors.walkingHours}</p>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">標高差目安 m（任意）</label>
                    <input type="number" name="elevationGain" className="form-control"
                      value={formData.elevationGain} onChange={handleChange}
                      min="0" step="50" placeholder="例: 800" />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">山行タイプ（任意）</label>
                    <select name="tripType" className="form-control"
                      value={formData.tripType} onChange={handleChange}>
                      {TRIP_TYPE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">主な移動手段（任意）</label>
                    <select name="transportMode" className="form-control"
                      value={formData.transportMode} onChange={handleChange}>
                      {TRANSPORT_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">集合場所タイプ（任意）</label>
                    <select name="meetingAreaType" className="form-control"
                      value={formData.meetingAreaType} onChange={handleChange}>
                      {MEETING_AREA_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">対象経験レベル（任意）</label>
                    <select name="experienceLevelTarget" className="form-control"
                      value={formData.experienceLevelTarget} onChange={handleChange}>
                      {EXPERIENCE_LEVEL_TARGET_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">相乗り可否（任意）</label>
                    <select name="carShareAvailable" className="form-control"
                      value={formData.carShareAvailable} onChange={handleChange}>
                      {CAR_SHARE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">天候判断（任意）</label>
                    <select name="weatherThreshold" className="form-control"
                      value={formData.weatherThreshold} onChange={handleChange}>
                      {WEATHER_THRESHOLD_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">想定予算（任意）</label>
                  <select name="estimatedBudgetBand" className="form-control"
                    value={formData.estimatedBudgetBand} onChange={handleChange}>
                    {ESTIMATED_BUDGET_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">危険箇所・注意地形（任意）</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {DANGER_OPTIONS.map(option => (
                      <label key={option.value} className="checkbox-label" style={{ width: 'auto' }}>
                        <input type="checkbox"
                          checked={formData.dangerFlags.includes(option.value)}
                          onChange={() => handleMultiSelectChange('dangerFlags', option.value)} />
                        {option.label}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">装備条件</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {EQUIPMENT_REQUIREMENT_OPTIONS.map(option => (
                      <label key={option.value} className="checkbox-label" style={{ width: 'auto' }}>
                        <input type="checkbox"
                          checked={formData.equipmentRequirements.includes(option.value)}
                          onChange={() => handleMultiSelectChange('equipmentRequirements', option.value)} />
                        {option.label}
                      </label>
                    ))}
                  </div>
                  <p className="form-help">参加者に必須とする装備がある場合はチェックしてください。</p>
                </div>

                <div className="form-group">
                  <label className="form-label">最低成立人数</label>
                  <input type="number" name="minParticipants" className="form-control"
                    value={formData.minParticipants} onChange={handleChange}
                    min="1" max="20" />
                  <p className="form-help">この人数に達しない場合は中止になります（目安：3人）</p>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">男女比（任意）</label>
                    <input type="text" name="genderRatioSingle" className="form-control"
                      value={formData.genderRatioSingle} onChange={handleChange}
                      placeholder="例: 男性2名・女性2名募集" />
                  </div>

                  <div className="form-group">
                    <label className="form-label">参加条件</label>
                    <select name="participantCondition" className="form-control"
                      value={formData.participantCondition} onChange={handleChange}>
                      <option value="anyone">誰でも歓迎</option>
                      <option value="interview">事前面談あり</option>
                      <option value="experienced">登山経験者のみ</option>
                      <option value="women_only">女性限定</option>
                      <option value="men_only">男性限定</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">装備チェックリストURL（任意）</label>
                  <input type="url" name="checklistUrl" className="form-control"
                    value={formData.checklistUrl} onChange={handleChange}
                    placeholder="https://yamano-media.com/blog/mountain-checker-app/" />
                  <p className="form-help">
                    参加者に共有したい
                    <a href="https://yamano-media.com/blog/mountain-checker-app/"
                      target="_blank" rel="noopener noreferrer">装備チェックリスト</a>
                    のURLを入力してください（このサイトのURLのみ入力可）
                  </p>
                </div>

                <div className="form-group" ref={fieldRef('description')}>
                  <label className="form-label">詳細説明 *</label>
                  <textarea name="description" className="form-control"
                    value={formData.description} onChange={handleChange}
                    required rows={6}
                    placeholder="登山の目的、スケジュール、特徴などを詳しく記載してください。" />
                  {fieldErrors.description && <p className="field-error">{fieldErrors.description}</p>}
                </div>
              </div>
            </>
          )}

          {formData.recruitmentType && (
            <>
              <div className="form-section">
                <h2>募集画像（任意）</h2>
                <div className="form-group" ref={fieldRef('image')}>
                  <input ref={imageInputRef} type="file" accept="image/*"
                    style={{ display: 'none' }} onChange={handleImageChange} />
                  {recruitmentImagePreview ? (
                    <div>
                      <img src={recruitmentImagePreview} alt="プレビュー"
                        style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px' }} />
                      <button type="button" className="btn btn-outline btn-sm"
                        onClick={() => { setRecruitmentImageFile(null); setRecruitmentImagePreview(''); }}>
                        画像を削除
                      </button>
                    </div>
                  ) : (
                    <button type="button" className="btn btn-outline"
                      onClick={() => imageInputRef.current?.click()}>
                      画像を選択（10MB以内）
                    </button>
                  )}
                  {fieldErrors.image && <p className="field-error">{fieldErrors.image}</p>}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary"
                  onClick={handleCancel} disabled={loading}>
                  キャンセル
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? '作成中...' : '募集を登録'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

export default CreateRecruitment;
